import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, Caption, H5, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { useCallback } from 'react';

import { NotificationPermissions } from '@/enums/notificationPermissions.enum';
import useWindowSize from '@/hooks/useWindowSize';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderCountryAllowedToReceiveSMS } from '@/redux/modules/account/user/user.selectors';
import { openSMSConfirmationModal } from '@/redux/modules/modal';
import { getPermission, requestBrowserPermission } from '@/redux/modules/notification';
import {
    getAllSMSAlertsSubscriptions,
    getSubscribedToSMSAlerts,
    getSubscriptionsById,
    toggleSmsAlert,
} from '@/redux/modules/smsAlerts';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import {
    TrackSmsNotificationToggleEventTrigger,
    useTrackSmsNotificationToggle,
} from '@/utils/amplitude/analytics/notifications';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import noop from '@/utils/noop';
import { enableBrowserNotificationsUrl } from '@/utils/urls';

import { TableRow } from './TableRow';

type Props = {
    alertTypeId: AlertTypes;
    className?: string;
};

export const AlertsRow = ({ alertTypeId, className }: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    let subscriptions = useAppSelector((state) => getSubscriptionsById(state, alertTypeId));
    if (!Array.isArray(subscriptions)) {
        subscriptions = [];
    }
    const smsAllowed = useAppSelector(getBidderCountryAllowedToReceiveSMS);
    const hasSMSalerts = useAppSelector(getSubscribedToSMSAlerts);
    const allSmsAlerts = useAppSelector(getAllSMSAlertsSubscriptions);

    const trackSmsNotificationToggle = useTrackSmsNotificationToggle();

    const smsBoxCheck = async () => {
        if (hasSMSalerts) {
            await dispatch(toggleSmsAlert(alertTypeId, DeliveryType.sms, !subscribedSms));

            trackSmsNotificationToggle({
                auctionRemindersEnabled:
                    alertTypeId === AlertTypes.AuctionReminder
                        ? !subscribedSms
                        : (allSmsAlerts?.[AlertTypes.AuctionReminder]?.subscribed ?? false),
                eventTrigger: TrackSmsNotificationToggleEventTrigger.EmailsAndNotifications,
                outbidEnabled:
                    alertTypeId === AlertTypes.Outbid
                        ? !subscribedSms
                        : (allSmsAlerts?.[AlertTypes.Outbid]?.subscribed ?? false),
                paymentRemindersEnabled:
                    alertTypeId === AlertTypes.PaymentReminder
                        ? !subscribedSms
                        : (allSmsAlerts?.[AlertTypes.PaymentReminder]?.subscribed ?? false),
            });
        } else {
            dispatch(openSMSConfirmationModal(alertTypeId));
        }
    };

    const smsSubscription = subscriptions.find(({ deliveryType }) => deliveryType === DeliveryType.sms);
    const subscribedSms = Boolean(smsSubscription?.subscribed);

    const browserSubscription = subscriptions.find(({ deliveryType }) => deliveryType === DeliveryType.browser);
    const subscribedBrowser = Boolean(browserSubscription?.subscribed);

    const emailSubscription = subscriptions.find(({ deliveryType }) => deliveryType === DeliveryType.email);
    const subscribedEmail = Boolean(emailSubscription?.subscribed);

    const permission = useAppSelector(getPermission);
    const permissionGranted = permission === NotificationPermissions.Granted;
    const permissionDenied = permission === NotificationPermissions.Denied;

    const { width } = useWindowSize();
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;

    const toggleEmailAlerts = useCallback(
        () => dispatch(toggleSmsAlert(alertTypeId, DeliveryType.email, !subscribedEmail)),
        [alertTypeId, dispatch, subscribedEmail]
    );

    const browserPermission = useCallback(() => dispatch(requestBrowserPermission()), [dispatch]);

    const toggleBroswerSms = useCallback(
        () => dispatch(toggleSmsAlert(alertTypeId, DeliveryType.browser, !subscribedBrowser)),
        [alertTypeId, dispatch, subscribedBrowser]
    );

    const handleBrowserSubscriptionClick = () => {
        if (!permissionGranted && !subscribedBrowser) {
            browserPermission();
        } else {
            toggleBroswerSms();
        }
    };

    return (
        <TableRow
            browser={
                browserSubscription && !permissionDenied ? (
                    <Checkbox.Option
                        aria-label={String(
                            formatMessage(['notificationsPage', 'toggleBrowserNotifications'], {
                                values: {
                                    name: subscriptions[0]?.name,
                                },
                            })
                        )}
                        checked={subscribedBrowser}
                        className="mb-0"
                        name="email-alert-checkbox"
                        onChange={handleBrowserSubscriptionClick}
                    />
                ) : browserSubscription ? (
                    <>
                        <div id={`browser-notif-disabled-${alertTypeId}`}>
                            <Checkbox.Option
                                aria-label={String(
                                    formatMessage(['notificationsPage', 'toggleBrowserNotifications'], {
                                        values: {
                                            name: subscriptions[0]?.name,
                                        },
                                    })
                                )}
                                checked={subscribedBrowser}
                                className="mb-0"
                                disabled
                                name="browserSubscription"
                                onChange={noop}
                            />
                        </div>
                        <Tooltip
                            anchorSelect={`#browser-notif-disabled-${alertTypeId}`}
                            className="max-w-[250px]"
                            place="top"
                        >
                            <div className="max-w-[250px] p-sm">
                                {formatMessage(['notificationsPage', 'disabled'])}
                                <div>
                                    <Link
                                        href={enableBrowserNotificationsUrl}
                                        target="_blank"
                                    >
                                        {formatMessage(['notifications', 'enable_browser_notifications'])}
                                    </Link>
                                </div>
                            </div>
                        </Tooltip>
                    </>
                ) : null
            }
            className={className}
            email={
                emailSubscription && (
                    <Checkbox.Option
                        aria-label={String(
                            formatMessage(['notificationsPage', 'toggleEmailNotifications'], {
                                values: {
                                    name: subscriptions[0]?.name,
                                },
                            })
                        )}
                        checked={subscribedEmail}
                        className="mb-0"
                        name="email-alert-checkbox"
                        onChange={toggleEmailAlerts}
                    />
                )
            }
            frequency={
                <BodySecondary>{formatMessage(['notificationsPage', 'table', 'frequency', alertTypeId])}</BodySecondary>
            }
            sms={
                smsAllowed &&
                smsSubscription && (
                    <Checkbox.Option
                        aria-label={String(
                            formatMessage(['notificationsPage', 'toggleSMSNotifications'], {
                                values: {
                                    name: subscriptions[0]?.name,
                                },
                            })
                        )}
                        checked={subscribedSms}
                        className="mb-0"
                        name="email-alert-checkbox"
                        onChange={smsBoxCheck}
                    />
                )
            }
            type={
                <>
                    {width > tabletNarrowWidthNum ? (
                        <H5>{subscriptions[0]?.name}</H5>
                    ) : (
                        <H6>{subscriptions[0]?.name}</H6>
                    )}
                    {width <= tabletNarrowWidthNum ? (
                        <Caption>{formatMessage(['notificationsPage', 'table', 'descriptions', alertTypeId])}</Caption>
                    ) : (
                        <BodySecondary color="secondary">
                            {formatMessage(['notificationsPage', 'table', 'descriptions', alertTypeId])}
                        </BodySecondary>
                    )}
                </>
            }
        />
    );
};
